import { useQuery } from '@tanstack/react-query';
import { hasAuthStateCookie } from '@/shared/libs/authState';
import { STALE_TIME } from '@/shared/constants/app/time.constant';
import { subscriptionApi } from './subscription.api';
import { subscriptionKeys } from './subscription.keys';

/** 활성 구독 1건. enabled 로 모달이 열렸을 때만 조회하도록 제어 가능. */
export function useMyActiveSubscription(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.myActive,
    queryFn: subscriptionApi.myActive,
    enabled: enabled && hasAuthStateCookie(),
    // 기업 콘솔의 좌석 배정으로 상태가 바뀔 수 있으므로 모달을 열 때마다 확인한다.
    staleTime: 0,
  });
}

/** 본인이 이미 보유한 상품 id — 스토어 '이용 중' 표시·중복구매 안내. 판정 규칙은 서버 SSOT. */
export function useMyOwnedProducts(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.myOwnedProducts,
    queryFn: subscriptionApi.myOwnedProducts,
    enabled: enabled && hasAuthStateCookie(),
    // 마이페이지 해지·만료 직후 스토어로 와도 막히지 않게 진입마다 확인한다.
    staleTime: 0,
  });
}

/** 본인의 구버전 이관 기간제 구독 상품 — 결제 확인 화면의 기간 겹침 안내. */
export function useMyLegacyTerms(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.myLegacyTerms,
    queryFn: subscriptionApi.myLegacyTerms,
    enabled: enabled && hasAuthStateCookie(),
    staleTime: STALE_TIME.SHORT,
  });
}

/** 본인 활성 라이선스 목록. */
export function useMyActiveLicenses(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.myActiveLicenses,
    queryFn: subscriptionApi.myActiveLicenses,
    enabled: enabled && hasAuthStateCookie(),
    // 다른 콘솔에서 배정·회수된 좌석이 이전 캐시에 남지 않게 한다.
    staleTime: 0,
  });
}

/**
 * 청약철회 프리뷰(§6.1).
 *
 * 호출부가 "개인 구독 + 7일 이내" 를 먼저 판정해 enabled 로 넘긴다 — 모든 구독
 * 카드가 이 요청을 던지면 대상이 아닌 구독까지 매번 서버를 때린다.
 * 환불 가능 여부는 시간에 따라 바뀌므로 캐시를 오래 들고 있지 않는다.
 */
export function useWithdrawalPreview(id: string | null, enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.withdrawalPreview(id ?? ''),
    queryFn: () => subscriptionApi.withdrawalPreview(id!),
    enabled: enabled && !!id && hasAuthStateCookie(),
    staleTime: STALE_TIME.SHORT,
  });
}

/**
 * 개인 연간 중도해지 환불 프리뷰(정책 제5조 ④).
 * 이용 개월이 날짜에 따라 바뀌므로 오래 캐시하지 않는다. 신청 후에는 신청 시각 기준 금액이 온다.
 */
export function useAnnualTerminationPreview(id: string | null, enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.annualTerminationPreview(id ?? ''),
    queryFn: () => subscriptionApi.annualTerminationPreview(id!),
    enabled: enabled && !!id && hasAuthStateCookie(),
    staleTime: STALE_TIME.SHORT,
  });
}

/** 재활성화 프리뷰(§6.3) — 청구 예정 금액. 결제 화면 진입 시 1회. */
export function useReactivationPreview(id: string | null) {
  return useQuery({
    queryKey: subscriptionKeys.reactivatePreview(id ?? ''),
    queryFn: () => subscriptionApi.reactivatePreview(id!),
    enabled: !!id && hasAuthStateCookie(),
    staleTime: STALE_TIME.SHORT,
  });
}
